import React, { useState } from "react";
import {
  Box, Button, TextField, Typography, Paper, CircularProgress, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemButton, Menu, MenuItem, IconButton, FormControl, InputLabel, Select
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ChatBox from "./ChatBox";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DOMPurify from 'dompurify';

import useExecuteScript from './hooks/useExecuteScript';

function AnalyzeForm({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const application = location.state?.application;

  const [form, setForm] = useState({
    project_name: application?.address || "",
    proposed_development: application?.proposedDevelopment || application.proposed_development || "",
    address: application?.address || "",
    lga: "",
    maxfsr: "",
    maxheight: "",
    lotsize: "",
    lotzone: "",
    files: null,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addressResults, setAddressResults] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [checking, setChecking] = useState(false);
  const [propId, setPropId] = useState(null);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [propertyFile, setPropertyFile] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [vectorStoreId, setVectorStoreId] = useState(null);
  const [htmlParts, setHtmlParts] = useState([]);
  const [selectedTask, setSelectedTask] = useState('CDC'); // Changed from 'DA' to 'CDC'

  // Account menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = async () => {
    await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/logout/", {
      method: "POST",
      credentials: "include",
    });
    window.location.reload();
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: files ? files : value,
    }));
  };

  const handleCheckAddress = async () => {
    setChecking(true);
    setAddressResults([]);
    setShowPopup(false);
    try {
      const url = `https://api.apps1.nsw.gov.au/planning/viewersf/V1/ePlanningApi/address?noOfRecords=10&a=${encodeURIComponent(form.address)}`;
      const res = await fetch(url);
      const json = await res.json();
      setAddressResults(json || []);
      setShowPopup(true);
    } catch (err) {
      setAddressResults([{ error: "Failed to fetch address suggestions." }]);
      setShowPopup(true);
    }
    setChecking(false);
  };

  const handleSelectAddress = async (item) => {
    setShowPopup(false);
    if (item.propId) {
      setPropId(item.propId);
      try {
        const apiUrl = `https://api.apps1.nsw.gov.au/planning/viewersf/V1/ePlanningApi/layerintersect?type=property&id=${item.propId}`;
        const response = await fetch(apiUrl);
        const blob = await response.blob();
        const text = await blob.text();
        let parsed = {};
        try {
          parsed = JSON.parse(text);
        } catch (err) {
          parsed = { error: "Could not parse property info." };
        }
        let lgaValue = "N/A", fsrValue = "N/A", maxheightValue = "N/A", lotsizeValue = "N/A", lotzoneValue = "N/A";
        if (parsed.length > 0 && parsed[0].results && parsed[0].results.length > 0) {
          lgaValue = parsed[0].results[0]["LGA Name"] || parsed[0].results[0]["lga name"] || "";
        }
        if (lgaValue.length === 0 && parsed.length > 1 && parsed[1].results && parsed[1].results.length > 0) {
          lgaValue = parsed[1].results[0]["LGA Name"] || parsed[1].results[0]["lga name"] || "";
        }
        for (var i = 0; i < parsed.length; i++) {
          if (parsed[i].layerName && parsed[i].layerName.toLowerCase() === "floor space ratio map") {
            fsrValue = parsed[i].results[0].title;
          }
          if (parsed[i].layerName && parsed[i].layerName.toLowerCase() === "height of buildings map") {
            maxheightValue = parsed[i].results[0].title;
          }
          if (parsed[i].layerName && parsed[i].layerName.toLowerCase() === "lot size map") {
            lotsizeValue = parsed[i].results[0].title;
          }
          if (parsed[i].layerName && parsed[i].layerName.toLowerCase() === "land zoning map") {
            lotzoneValue = parsed[i].results[0].title;
          }
        }
        setForm((prev) => ({
          ...prev,
          lga: lgaValue,
          maxfsr: fsrValue,
          maxheight: maxheightValue,
          lotsize: lotsizeValue,
          lotzone: lotzoneValue,
        }));
        setPropertyInfo([{ lga: lgaValue, fsr: fsrValue, maxheight: maxheightValue, lotsize: lotsizeValue, lotzone: lotzoneValue }]);
        setPropertyFile(new File([text], "site_info.json", { type: blob.type }));
      } catch (err) {
        setPropertyInfo([{ error: "Failed to download property info." }]);
        setPropertyFile(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setHtmlParts([]);

    const data = new FormData();
    data.append("project_name", form.project_name);
    data.append("proposed_development", form.proposed_development);
    data.append("address", form.address);
    data.append("lga", form.lga);
    data.append("maxfsr", form.maxfsr);
    data.append("maxheight", form.maxheight);
    data.append("lotsize", form.lotsize);
    data.append("lotzone", form.lotzone);
    data.append("task", selectedTask); // Add task type

    // Attach new files
    if (form.files) {
      for (let i = 0; i < form.files.length; i++) {
        data.append("files", form.files[i]);
      }
    }
    if (propertyFile) {
      data.append("files", propertyFile);
    }

    // Handle existing files from the application (from DB)
    if (application?.attachments) {
      let existingFiles = [];
      try {
        // Try parsing as JSON first
        if (typeof application.attachments === 'string') {
          existingFiles = JSON.parse(application.attachments);
        } else if (Array.isArray(application.attachments)) {
          existingFiles = application.attachments;
        }
      } catch (e) {
        // Fallback to legacy comma-separated string
        existingFiles = application.attachments.split(",").filter(f => f);
      }
      if (existingFiles.length > 0) {
        data.append("existing_files", JSON.stringify(existingFiles));
      }
    }
    if (application?.id) {
      data.append("application_id", application.id);
    }

    try {
      const response = await fetch(
        (process.env.REACT_APP_API_URL || "http://localhost:8000") + "/analyze/",
        { method: "POST", body: data, credentials: "include" }
      );

      // check if streaming response
      if (response.headers.get('content-type')?.includes('text/event-stream')) {
        const reader = response.body.getReader();
        let decoder = new TextDecoder();
        let aiMsg = "";
        let threadId = null;
        let vectorStoreId = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });

          // Split by double newlines (SSE event delimiter)
          chunk.split("\n\n").forEach((eventBlock) => {
            if (eventBlock.startsWith("event: thread_id")) {
              // Extract thread_id
              const match = eventBlock.match(/data:\s*(.*)/);
              if (match) {
                threadId = match[1].trim();
                setThreadId(threadId); // or whatever state you use
              }
            } else if (eventBlock.startsWith("event: vector_store_id")) {
              // Extract vector_store_id
              const match = eventBlock.match(/data:\s*(.*)/);
              if (match) {
                vectorStoreId = match[1].trim();
                setVectorStoreId(vectorStoreId);
              }
            } else if (eventBlock.startsWith("data:")) {
              const clean = eventBlock.replace(/^data:\s*/g, "");
              if (clean.trim() === "[DONE]") {
                return;
              }
              if (clean.trim() !== "") {
                aiMsg = aiMsg.length > 0 ? aiMsg + "\n\n" + clean.trim() : clean.trim();
                setResult(aiMsg);
              }
            } else {
              // this is now HTML or other content
              const htmlContent = eventBlock.trim();
              if (htmlContent) {
                setHtmlParts(prev => [...prev, htmlContent]);
                // Combine all parts and set as result
                setResult(prev => {
                  const combined = htmlParts.join('');
                  // Only update if we have a complete script tag
                  if (!combined.includes('<script>') || 
                      (combined.includes('<script>') && combined.includes('</script>'))) {
                    return combined;
                  }
                  return prev;
                });
              }
            }
          });
        }
      } else {
        // handle regular JSON response
        const text = await response.text();
        try {
          const data = JSON.parse(text);  // Parse the JSON string
          if (data.html) {
            setResult(data.html);  // Get the HTML component
          } else if (data.error) {
            setResult(data.error);
          }
          if (data.thread_id) setThreadId(data.thread_id);
          if (data.vector_store_id) setVectorStoreId(data.vector_store_id);
        } catch (e) {
          console.error('Failed to parse JSON response:', e);
          setResult(text);  // Fallback to raw text if parsing fails
        }
      }
    } catch (err) {
      setResult("Error: " + err.message);
    }
    setLoading(false);
  };

  // Helper to get all files to be attached
  const getAllFiles = () => {
    let filesArr = [];
    if (form.files) {
      filesArr = filesArr.concat(Array.from(form.files));
    }
    if (propertyFile) {
      filesArr.push(propertyFile);
    }
    // Handle attachments as JSON
    if (application?.attachments) {
      let existingFiles = [];
      try {
        // Try parsing as JSON first
        if (typeof application.attachments === 'string') {
          existingFiles = JSON.parse(application.attachments);
        } else if (Array.isArray(application.attachments)) {
          existingFiles = application.attachments;
        }
      } catch (e) {
        // Fallback to legacy comma-separated string
        existingFiles = application.attachments.split(",").filter(f => f);
      }
      // Add as virtual file objects for display
      filesArr = filesArr.concat(
        existingFiles.map(name => ({
          name: typeof name === 'object' ? name.name : name,
          fromDatabase: true
        }))
      );
    }
    return filesArr;
  };

  // Execute script if result changes
  useExecuteScript(result);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "linear-gradient(135deg, #e0e7ef 0%, #f7fafc 100%)",
        backgroundAttachment: "fixed",
        py: 6,
      }}
    >
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        variant="outlined"
        sx={{
          position: "absolute",
          top: 16,
          left: 32,
          zIndex: 10,
          background: "#fff",
          color: "#222",
          borderColor: "#bbb",
          '&:hover': {
            background: "#f5f5f5",
            borderColor: "#888",
          }
        }}
        onClick={() => navigate("/applications")}
      >
        Back to Applications
      </Button>
      {/* Top right account button */}
      <Box sx={{ position: "absolute", top: 16, right: 32 }}>
        <IconButton
          size="large"
          edge="end"
          color="inherit"
          onClick={handleMenu}
        >
          <AccountCircle fontSize="large" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem disabled>
            <Typography variant="subtitle2">
              {user?.username} ({user?.role})
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleClose}>My Account</MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </Box>
      <Box sx={{ width: "100%", mt: 4 }}>
        <Paper sx={{ p: 3, mb: 3, boxShadow: 3 }}>
          <Typography variant="h5" gutterBottom>
            POPPi - Development Application Intelligence
          </Typography>
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <TextField
              label="Project Name"
              name="project_name"
              value={form.project_name}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Proposed Development"
              name="proposed_development"
              value={form.proposed_development}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <TextField
                label="Address"
                name="address"
                value={form.address}
                onChange={handleChange}
                required
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={handleCheckAddress}
                disabled={checking || !form.address}
                sx={{ ml: 2, minWidth: 200 }}
              >
                {checking ? <CircularProgress size={20} /> : "Get Details"}
              </Button>
            </Box>
            <TextField
              label="LGA"
              name="lga"
              value={form.lga}
              onChange={handleChange}
              fullWidth
              required
              disabled
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField label="Max FSR" name="maxfsr" value={form.maxfsr} onChange={handleChange} fullWidth disabled />
              <TextField label="Max Height" name="maxheight" value={form.maxheight} onChange={handleChange} fullWidth disabled />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField label="Lot Size" name="lotsize" value={form.lotsize} onChange={handleChange} fullWidth disabled />
              <TextField label="Lot Zone" name="lotzone" value={form.lotzone} onChange={handleChange} fullWidth disabled />
            </Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
                label="Task Type"
              >
                <MenuItem value="CDC">CC - Complying Development Certificate</MenuItem>
                <MenuItem value="DA">CC - Development Application</MenuItem>
                <MenuItem value="SSD">CC - State Significant Development</MenuItem>
                <MenuItem value="BCA">OCC - BCA / NCC</MenuItem>
              </Select>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !propId}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Analyze"}
            </Button>
            <Button variant="contained" component="label" sx={{ float: "right" }}>
              Attach Files
              <input
                name="files"
                type="file"
                multiple
                hidden
                onChange={handleChange}
              />
            </Button>
          </form>
          {getAllFiles().length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Files to be attached:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {getAllFiles().map((file, idx) => (
                  <li key={idx} style={{ fontSize: 14 }}>{file.name}</li>
                ))}
              </ul>
            </Box>
          )}
          {result && (
            <Paper sx={{ mt: 3, p: 2, background: "#f9f9f9" }}>
              <div dangerouslySetInnerHTML={{ 
                __html: result
              }} />
            </Paper>
          )}
        </Paper>
        <Dialog open={showPopup} onClose={() => setShowPopup(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Address Suggestions</DialogTitle>
          <DialogContent>
            <List>
              {addressResults.length === 0 && <ListItem>No results found.</ListItem>}
              {addressResults.map((item, idx) =>
                item.error ? (
                  <ListItem key={idx} sx={{ color: "red" }}>{item.error}</ListItem>
                ) : (
                  <ListItemButton key={idx} onClick={() => handleSelectAddress(item)}>
                    {item.address || item.fullAddress || "No address"}
                  </ListItemButton>
                )
              )}
            </List>
            <Button onClick={() => setShowPopup(false)} sx={{ mt: 2 }}>Close</Button>
          </DialogContent>
        </Dialog>
        {threadId && (
          <Paper sx={{ p: 3, mb: 3, boxShadow: 3 }}>
            <ChatBox 
              threadId={threadId} 
              vectorStoreId={vectorStoreId}
              sx={{ minHeight: '300px' }}  // Add minimum height
            />
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default AnalyzeForm;